import React from "react";
import PageHero, { FeatureList } from "@/components/PageHero";
import { CTAFooter } from "@/pages/Prestations";
import { CatalogPackages } from "@/components/CatalogPackages";
import NearbyCitiesLinks from "@/components/NearbyCitiesLinks";
import { useSeo, JsonLd } from "@/lib/seo";
import { Heart, Music, Lightbulb, Camera, Gem, Crown } from "lucide-react";

const TIMELINE = [
  { time: "Cérémonie", title: "Cérémonie laïque ou religieuse", desc: "Sonorisation discrète, micros HF pour les officiants, musiques d'entrée et de sortie personnalisées." },
  { time: "Vin d'honneur", title: "Cocktail & vin d'honneur", desc: "Sets lounge & ambiance élégante. Conversations préservées, atmosphère raffinée." },
  { time: "Repas", title: "Repas & animations", desc: "Animation des temps forts : entrée des mariés, premier verre, jeux. Maître de cérémonie sur demande." },
  { time: "Ouverture", title: "Ouverture de bal", desc: "Mise en scène lumière, slow personnalisé, effets scéniques pour magnifier l'instant." },
  { time: "Soirée", title: "Soirée dansante", desc: "DJ en lecture continue. Playlist personnalisée, transitions millimétrées, piste enflammée." },
];

export default function Mariage() {
  useSeo({
    title: "DJ mariage Hauts-de-France | My Animation",
    description:
      "DJ mariage en Hauts-de-France, Nord, Pas-de-Calais et Belgique. Animation musicale élégante, ouverture de bal, soirée dansante, sonorisation et mise en lumière.",
    path: "/mariage",
    keywords: "DJ mariage Hauts-de-France, DJ mariage Nord, DJ mariage Pas-de-Calais, DJ mariage Lille, DJ mariage Tourcoing",
  });

  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "DJ Mariage",
    provider: { "@type": "LocalBusiness", name: "My Animation" },
    areaServed: "Hauts-de-France, Nord, Pas-de-Calais, Belgique",
    description:
      "Animation musicale complète pour mariage : cérémonie, vin d'honneur, repas, ouverture de bal, soirée dansante, sonorisation et mise en lumière.",
  };

  return (
    <>
      <JsonLd data={serviceLd} />
      <PageHero
        eyebrow="DJ Mariage"
        title={<>L'élégance, jusqu'à <span className="italic gold-text">la dernière note.</span></>}
        subtitle="Animation musicale complète pour mariage : cérémonie, vin d'honneur, cocktail, repas, entrée des mariés, ouverture de bal, soirée dansante. Une ambiance élégante, festive et personnalisée à Lille, Tourcoing, Roubaix, Armentières, Bousbecque et dans tout le Nord, le Pas-de-Calais et la Belgique."
        img="/assets/mariages/mariage-01.jpg"
        imgPosition="50% 8%"
      />

      <section className="py-24 md:py-32 px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5 reveal">
            <span className="eyebrow">Notre approche</span>
            <h2 className="font-serif-luxe text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.02]">
              Une seule ambition :<br />
              <span className="italic gold-text">vous ressembler.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 reveal">
            <p className="text-white/70 text-base md:text-lg leading-relaxed">
              Chaque mariage est unique. My Animation prend le temps de vous écouter
              pour comprendre vos goûts, votre histoire, vos envies. Nous bâtissons
              ensemble une bande-son taillée pour vos invités, des moments-clés
              magnifiés et une équipe attentive du début à la fin.
            </p>
            <div className="mt-10">
              <FeatureList items={[
                "Entretien préparatoire offert",
                "Playlist personnalisée",
                "Sonorisation cérémonie laïque",
                "Mise en lumière sur-mesure",
                "Animation cocktail & repas",
                "Soirée dansante jusque tard",
                "Maître de cérémonie en option",
                "Photobooth & sax live disponibles",
              ]} />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 px-6 md:px-10 lg:px-14 bg-[#080808] border-y border-[#DCB457]/15">
        <div className="max-w-[1100px] mx-auto">
          <div className="text-center mb-16 reveal">
            <span className="eyebrow">Déroulé type</span>
            <h2 className="font-serif-luxe text-4xl md:text-5xl mt-3">Le grand jour, <span className="italic gold-text">étape par étape.</span></h2>
          </div>
          <div className="space-y-6">
            {TIMELINE.map((t, i) => (
              <div key={t.time} data-testid={`timeline-${i}`} className="card-luxe p-7 md:p-10 grid grid-cols-1 md:grid-cols-12 gap-6 reveal">
                <div className="md:col-span-3">
                  <div className="font-serif-luxe text-2xl md:text-3xl gold-text">{t.time}</div>
                </div>
                <div className="md:col-span-9">
                  <h3 className="font-serif-luxe text-2xl md:text-3xl">{t.title}</h3>
                  <p className="text-white/60 mt-2 leading-relaxed">{t.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto">
        <div className="mb-14 reveal">
          <span className="eyebrow">Options complémentaires</span>
          <h2 className="font-serif-luxe text-4xl md:text-5xl lg:text-6xl mt-3 leading-[1.02]">
            Sublimez votre soirée<br/><span className="italic gold-text">à votre image.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { i: Lightbulb, t: "Mise en lumière", d: "Éclairage architectural, lyres, effets scéniques." },
            { i: Camera, t: "Photobooth", d: "Forfaits dès 179 €, photos numériques illimitées." },
            { i: Music, t: "Chanteur Live / Sax", d: "Saxophoniste, chanteur, guitariste en duo avec le DJ." },
            { i: Heart, t: "Cérémonie laïque", d: "Sonorisation dédiée, micros et musiques personnalisées." },
          ].map(({ i: Icon, t, d }) => (
            <div key={t} className="card-luxe p-7 reveal">
              <Icon size={28} strokeWidth={1} className="text-[#DCB457] mb-5" />
              <h3 className="font-serif-luxe text-2xl">{t}</h3>
              <p className="text-white/55 mt-2 text-sm leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <CatalogPackages
        category="mariage"
        title="Nos forfaits mariage · 2026"
        subtitle={<>Trois écrins,<br /><span className="italic gold-text">une seule promesse.</span></>}
        icons={[Heart, Gem, Crown]}
        testidPrefix="mariage-pack-"
        featuredBadgeLabel="Le plus choisi"
        ctaLabel="Pré-réserver ce forfait"
        footerNote="Tarifs TTC · Devis personnalisé disponible · Intervention dans tout le Nord, le Pas-de-Calais, les Hauts-de-France et la Belgique"
      />

      <NearbyCitiesLinks
        eyebrow="Votre DJ mariage près de chez vous"
        title={<>Votre DJ mariage <span className="italic gold-text">près de chez vous.</span></>}
        urlPrefix="dj-mariage"
        intro="Basée à Bousbecque, My Animation intervient pour les mariages dans tout le Nord, le Pas-de-Calais et les Hauts-de-France, ainsi qu'en Belgique frontalière. Découvrez nos pages dédiées ville par ville, avec les lieux de réception connus dans le secteur."
        citySlugs={["lille", "tourcoing", "roubaix", "armentieres", "dunkerque", "lens", "arras", "valenciennes", "cambrai", "bethune"]}
        testidPrefix="mariage-nearby"
      />

      <CTAFooter />
    </>
  );
}

